/**
 * CLE-2323 / CLE-2331 — pure helpers for the rate-tools sub-tabs. Extracted into their
 * own file so they can be unit-tested without driving PrimeReact widgets (`FileUpload`
 * customUpload flow + Dropdown internals are awkward to drive through jsdom).
 */

import { ISpecialtyOverride } from "store/queries/rateTools";
import { IGrouperGenerateStats } from "types/PayerGrouper/PayerGrouper";

/**
 * CLE-2514 — the four specialties the Specialty Overwrite panel is seeded with. Free-form:
 * the analyst can add/remove any service-line label (matched against the file's Service Line
 * column server-side).
 */
export const DEFAULT_SPECIALTIES = [
  "Radiology",
  "Imaging",
  "Nuclear Medicine",
  "Diagnostic Ultrasound",
];

/**
 * CLE-2514 — one side's (facility or non-facility) "Modifiers to Include" checkbox state.
 * `global` and `noModifier` both resolve to the same blank-modifier row (our data model
 * stores global as an empty modifier), so checking either includes the no-modifier price.
 */
export interface IModifierSelection {
  global: boolean;
  professional: boolean; // 26
  technical: boolean; // TC
  fiftyThree: boolean; // 53
  other: string; // free text, e.g. "50" or "50, XU"
  noModifier: boolean;
}

export const EMPTY_MODIFIER_SELECTION: IModifierSelection = {
  global: false,
  professional: false,
  technical: false,
  fiftyThree: false,
  other: "",
  noModifier: false,
};

/**
 * Convert one side's checkbox state into the canonical modifier tokens sent to the server.
 * Global / no-modifier → "GLOBAL" (the server folds it to a blank modifier); 26 / TC / 53 as
 * literals; the free-text "Other" field is split on commas/whitespace. Duplicates are removed.
 */
export function modifierSelectionToTokens(sel: IModifierSelection): string[] {
  const tokens: string[] = [];
  if (sel.global) tokens.push("GLOBAL");
  if (sel.noModifier) tokens.push("GLOBAL");
  if (sel.professional) tokens.push("26");
  if (sel.technical) tokens.push("TC");
  if (sel.fiftyThree) tokens.push("53");
  sel.other
    .split(/[\n,\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s !== "")
    .forEach((t) => tokens.push(t));
  return Array.from(new Set(tokens));
}

/**
 * Build the `specialtyOverrides` request payload from the panel state. Returns an empty array
 * (⇒ the field is omitted) when there are no specialties, or when neither output side has any
 * modifier selected — in which case the split behaves exactly as before.
 */
export function buildSpecialtyOverrides(
  specialties: string[],
  facility: IModifierSelection,
  nonFacility: IModifierSelection
): ISpecialtyOverride[] {
  const cleanSpecialties = specialties
    .map((s) => s.trim())
    .filter((s) => s !== "");
  if (cleanSpecialties.length === 0) return [];
  const facilityModifiers = modifierSelectionToTokens(facility);
  const nonFacilityModifiers = modifierSelectionToTokens(nonFacility);
  if (facilityModifiers.length === 0 && nonFacilityModifiers.length === 0)
    return [];
  return [
    { specialties: cleanSpecialties, facilityModifiers, nonFacilityModifiers },
  ];
}

export interface ISplitStats {
  fileMode: string;
  inputRows: number;
  facilityRows: number;
  nonFacilityRows: number;
  distinctCpts: number;
  droppedMissingCpt: number;
  droppedNoRate: number;
  /**
   * CLE-2464 — of the service lines the analyst requested for the non-facility TC override,
   * the subset that actually matched at least one input row (original-cased). Requests not
   * in this list are likely typos that had no effect.
   */
  tcServiceLinesMatched: string[];
  /** CLE-2464 — count of distinct non-fac CPTs whose rate came from a TC row via the override. */
  tcOverriddenCpts: number;
  /**
   * CLE-2514 — of the specialties listed under Specialty Overwrite, the subset that matched at
   * least one input row (original-cased). Requests not here are likely typos with no effect.
   */
  specialtyMatchedServiceLines: string[];
  /** CLE-2514 — total output rows produced by the Specialty Overwrite (per-modifier) path. */
  overrideRows: number;
}

export interface IExpandStats {
  inputRows: number;
  /**
   * Rows that received a real rules-sheet (or synonym-resolved) multiplier. Read
   * from `x-expand-mapped-rows` if present, falling back to `x-expand-expanded-rows`
   * (the older name for the same count) for back-compat during the rollout.
   */
  mappedRows: number;
  /**
   * Deprecated — same value as `mappedRows`. Kept on the type until any UI callsite
   * is fully off it. Remove in a future cleanup.
   */
  expandedRows: number;
  /**
   * Rows whose service line had no rules-sheet match. Per CLE-2323 follow-up r2,
   * these still appear in the output CSV with the original label, multiplier=1,
   * and rate=baseRate, with an `Unmapped=true` column for analyst review.
   */
  unmappedRows: number;
  droppedMissingCpt: number;
  droppedNoBaseRate: number;
  unmappedServiceLines: string[];
  /** Rows whose Service Line came from the per-payer library (CLE-2323 follow-on). */
  resolvedFromLibrary: number;
}

/** Read the `x-split-*` diagnostic headers the backend sets on a successful split. */
export function parseSplitStats(headers: Record<string, string>): ISplitStats {
  const num = (k: string) => Number(headers[k] ?? 0);
  const jsonStringArray = (k: string): string[] => {
    try {
      const raw = headers[k];
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
          return parsed.filter((s): s is string => typeof s === "string");
      }
    } catch (_e) {
      /* malformed header — leave empty */
    }
    return [];
  };
  return {
    fileMode: headers["x-split-file-mode"] ?? "",
    inputRows: num("x-split-input-rows"),
    facilityRows: num("x-split-facility-rows"),
    nonFacilityRows: num("x-split-nonfacility-rows"),
    distinctCpts: num("x-split-distinct-cpts"),
    droppedMissingCpt: num("x-split-dropped-missing-cpt"),
    droppedNoRate: num("x-split-dropped-no-rate"),
    tcServiceLinesMatched: jsonStringArray("x-split-tc-service-lines-matched"),
    tcOverriddenCpts: num("x-split-tc-overridden-cpts"),
    specialtyMatchedServiceLines: jsonStringArray("x-split-specialty-matched"),
    overrideRows: num("x-split-override-rows"),
  };
}

/**
 * Read the `x-expand-*` diagnostic headers from a successful expand. The unmapped
 * service-line list is JSON-encoded; we defensively swallow parse errors so a bad
 * header doesn't crash the UI.
 */
export function parseExpandStats(
  headers: Record<string, string>
): IExpandStats {
  const num = (k: string) => Number(headers[k] ?? 0);
  let unmappedSls: string[] = [];
  try {
    const raw = headers["x-expand-unmapped-service-lines"];
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        unmappedSls = parsed.filter((s): s is string => typeof s === "string");
    }
  } catch (_e) {
    /* malformed header — leave empty */
  }
  // Prefer the new `x-expand-mapped-rows` header; fall back to the deprecated
  // `x-expand-expanded-rows` for back-compat during the CLE-2323 r2 rollout.
  const mapped =
    headers["x-expand-mapped-rows"] !== undefined
      ? num("x-expand-mapped-rows")
      : num("x-expand-expanded-rows");
  return {
    inputRows: num("x-expand-input-rows"),
    mappedRows: mapped,
    expandedRows: mapped,
    unmappedRows: num("x-expand-unmapped-rows"),
    droppedMissingCpt: num("x-expand-dropped-missing-cpt"),
    droppedNoBaseRate: num("x-expand-dropped-no-base-rate"),
    unmappedServiceLines: unmappedSls,
    resolvedFromLibrary: num("x-expand-resolved-from-library"),
  };
}

/**
 * CLE-2566 — read the `x-grouper-*` diagnostic headers from a successful grouper
 * generate. The two category lists are JSON-encoded; parse errors are swallowed so a bad
 * header degrades the banner rather than crashing the tab.
 */
export function parseGrouperStats(
  headers: Record<string, string>
): IGrouperGenerateStats {
  const num = (k: string) => Number(headers[k] ?? 0);
  const jsonStringArray = (k: string): string[] => {
    try {
      const raw = headers[k];
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
          return parsed.filter((s): s is string => typeof s === "string");
      }
    } catch (_e) {
      /* malformed header — leave empty */
    }
    return [];
  };
  return {
    totalRows: num("x-grouper-total-rows"),
    pricedRows: num("x-grouper-priced-rows"),
    zeroRows: num("x-grouper-zero-rows"),
    unpricedCategories: jsonStringArray("x-grouper-unpriced-categories"),
    unknownCategories: jsonStringArray("x-grouper-unknown-categories"),
  };
}

/** Friendly label for the splitter's file-mode banner. */
export function fileModeLabel(fileMode: string): string {
  if (fileMode === "TYPE_A_BALANCED")
    return "Detected as balanced (Aetna-style)";
  if (fileMode === "TYPE_B_FACILITY_AS_EXCEPTION")
    return "Detected as facility-as-exception (BCBS-style)";
  return `Detected as ${fileMode || "unknown"}`;
}
